import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import '../loyalty.css';
import { expiryPreview } from '../format';

interface Props {
  open: boolean;
  /** The advantage being bought. */
  name: string;
  costPts: number;
  /** Balance BEFORE the exchange. */
  balance: number;
  validityDays?: number | null;
  /** Restaurant timezone (IANA): the promised date is on its calendar. */
  tz: string;
  busy: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Spending points always asks first (SC-1091, review B/M2/M4): a one-tap
 * exchange took points with no confirmation and no word on getting them back.
 * The sheet states the cost, what is left, and the rule — an advantage not used
 * before its end date gives its points back.
 */
export default function ConfirmSheet({
  open,
  name,
  costPts,
  balance,
  validityDays,
  tz,
  busy,
  error,
  onConfirm,
  onCancel,
}: Props) {
  const { t, i18n } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Focus the dialog itself, not the confirm button: the sheet opens under the
    // finger that tapped « Échanger », and a double tap must not spend points.
    dialogRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, busy, onCancel]);

  if (!open) return null;

  const date = expiryPreview(validityDays, tz, i18n.language);
  const spend = t('loyalty.pointsUnit', { n: costPts });

  return createPortal(
    <div
      className="loyalty-scope fixed inset-0 z-[1100] flex items-end justify-center bg-black/60 sm:items-center sm:p-4"
      onClick={() => {
        if (!busy) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="loyalty-confirm-title"
        tabIndex={-1}
        data-testid="loyalty-confirm-sheet"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[420px] rounded-t-btn border-2 border-solid border-ink bg-paper p-5 shadow-stamp-lg outline-none sm:rounded-btn"
      >
        <h2 id="loyalty-confirm-title" className="m-0 font-display text-xl text-ink">
          {t('loyalty.confirm.title')}
        </h2>
        <p dir="auto" className="m-0 mt-2 break-words font-display text-base text-ink">
          {name}
        </p>
        <p className="m-0 mt-2 font-mono text-sm text-ink">
          {t('loyalty.confirm.cost', { n: costPts, left: Math.max(0, balance - costPts) })}
        </p>
        <p
          data-testid="loyalty-confirm-rule"
          className="m-0 mt-3 rounded-btn-sm border-2 border-dashed border-[var(--ink-faint)] bg-cream px-3 py-2 font-mono text-sm text-ink"
        >
          {date ? t('loyalty.confirm.rule', { date }) : t('loyalty.confirm.ruleNoDate')}
        </p>
        {error && (
          <p role="alert" className="m-0 mt-3 font-mono text-sm text-sale-red">
            ⚠ {error}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="min-h-[48px] rounded-btn-sm border-2 border-solid border-ink bg-paper px-4 font-display text-sm text-ink disabled:opacity-60"
          >
            {t('loyalty.confirm.cancel')}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            data-testid="loyalty-confirm-ok"
            className="min-h-[48px] rounded-btn-sm border-2 border-solid border-ink bg-mint px-4 font-display text-sm text-[var(--on-primary)] disabled:opacity-60"
          >
            {busy ? t('loyalty.redeeming') : `${t('loyalty.redeemButton')} · ${spend}`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
